import api from '@disco/api/server'
import {
  GROUP_CREATE,
  GROUP_CREATE_FAIL,
  GROUP_CREATE_SUCCESS,
  GROUP_DELETE,
  GROUP_DELETE_FAIL,
  GROUP_DELETE_SUCCESS,
  GROUP_GET,
  GROUP_GET_FAIL,
  GROUP_GET_SUCCESS,
  GROUP_UPDATE,
  GROUP_UPDATE_FAIL,
  GROUP_UPDATE_SUCCESS,
  GROUPS_LOAD,
  GROUPS_LOAD_FAIL,
  GROUPS_LOAD_SUCCESS,
  GROUP_BUSINESS_SHARE_UPDATE,
  GROUP_BUSINESS_SHARE_UPDATE_SUCCESS,
  LOAD_GROUP_BUSINESS_SHARES_SUCCESS,
  LOAD_GROUP_BUSINESS_SHARES,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import {
  getGroup,
  getGroupsLastUpdate,
} from '@disco/state-management/selectors/groups'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import type { Group, GroupId } from 'types/group'
import type { GetState } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
/**
 * Loads groups from the server with optional refresh and error handling.
 * @param props - Optional configuration object
 * @param props.refresh - Whether to refresh existing data
 * @param props.rethrowError - Whether to rethrow errors for handling by caller
 * @returns Thunk that loads groups and dispatches success/failure actions
 */
export function loadGroups(
  props?:
    | {
        refresh?: boolean
        rethrowError?: boolean
      }
    | null
    | undefined,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: GROUPS_LOAD,
    })
    const includeSharedChannels = !__REACT_NATIVE__
    const lastGroupUpdate = props?.refresh
      ? getGroupsLastUpdate(getState())
      : null

    const func = () => api.getGroups(lastGroupUpdate, includeSharedChannels)

    return api
      .retryCall(func)
      .then(result =>
        dispatch({
          type: GROUPS_LOAD_SUCCESS,
          groups: result,
          refresh: props?.refresh,
        }),
      )
      .catch(error => {
        dispatch({
          type: GROUPS_LOAD_FAIL,
          error: error,
        })
        if (props?.rethrowError) throw error
      })
  }
}
/**
 * Loads a specific group by ID from the server.
 * @param id - The ID of the group to load
 * @returns Thunk that fetches and dispatches the group data
 */
export function loadGroup(id: GroupId): (dispatch: any) => Promise<unknown> {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: GROUP_GET,
    })
    return api
      .getGroup(id)
      .then(result =>
        dispatch({
          type: GROUP_GET_SUCCESS,
          group: result,
        }),
      )
      .catch(error =>
        dispatch({
          type: GROUP_GET_FAIL,
          error: error,
        }),
      )
  }
}
/**
 * Deletes a group or leaves it if user is a partner.
 * @param group - The group to delete or leave
 * @returns Thunk that deletes/leaves the group and dispatches results
 */
export function deleteGroup(group: Group): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: GROUP_DELETE,
      group: group,
    })
    const action =
      group.business_role === 'partner' ? api.leaveGroup : api.deleteGroup
    action(group)
      .then(response => {
        if (response?.groups_ids) {
          response.groups_ids.forEach(groupId => {
            dispatch({
              type: GROUP_DELETE_SUCCESS,
              group: {
                id: groupId,
              },
            })
          })
        } else {
          dispatch({
            type: GROUP_DELETE_SUCCESS,
            group: group,
          })
        }
      })
      .catch(error => {
        dispatch({
          type: GROUP_DELETE_FAIL,
          group: group,
          error: error,
        })
      })
  }
}

function groupUpdateMessage(group: Group) {
  // Uses direct API result, so group.parent is used instead of group.parentGroup
  if (group.parent) {
    return 'Folder updated!'
  } else if (group.group_type === CUSTOM_CHANNEL) {
    return 'Channel updated!'
  } else {
    return 'Inbox updated!'
  }
}

/**
 * Adds multiple groups to the store and returns the root group.
 * @param groups - Array of groups to add to the store
 * @returns Thunk that adds groups and returns the root channel
 */
export function addGroups(
  groups: Group[],
): (dispatch: any, getState: GetState) => Group | void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // Add groups to the store
    groups.forEach(group => {
      dispatch({
        type: GROUP_GET_SUCCESS,
        group,
      })
    })
    // Find the root channel
    const rootGroup = groups.find(g => g.parent === null)
    if (!rootGroup) return
    // Get the root channel from the store
    const newRootGroup = getGroup(getState(), {
      groupId: rootGroup.id,
    })
    if (!newRootGroup) return
    // Return the root channel
    return newRootGroup
  }
}
/**
 * Updates an existing group with new data and handles notifications.
 * @param group - Partial group data to update
 * @param parentChanged - Whether the parent was changed (affects notifications)
 * @param permissionsChanged - Whether permissions were changed
 * @returns Thunk that updates the group and shows appropriate notifications
 */
export function updateGroup(
  group: Partial<Group>,
  parentChanged = false,
  permissionsChanged = false,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: GROUP_UPDATE,
      group,
    })

    try {
      const result = await api.updateGroup(group)
      dispatch({
        type: GROUP_UPDATE_SUCCESS,
        group: result,
        parentChanged,
      })

      if (result.managers.length > 1 || result.members.length > 0) {
        if (result.group_type === CUSTOM_CHANNEL) {
          dispatch(sendMixpanelEvent('Change channel permissions'))
        }

        if (INBOX_TYPES.indexOf(result.group_type) > -1) {
          dispatch(sendMixpanelEvent('Change inbox permissions'))
        }
      }

      if (permissionsChanged) {
        pushUINotification({
          message: 'Changes saved!',
          level: 'success',
        })
      }

      // Don't fire notifications when a folder is moved to another channel
      if (!parentChanged) {
        pushUINotification({
          message: groupUpdateMessage(result),
          level: 'success',
        })
      }
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: GROUP_UPDATE_FAIL,
        group,
        error,
      })
    }
  }
}

function groupCreateMessage(group: Group) {
  // Uses direct API result, so group.parent is used instead of group.parentGroup
  if (group.parent) {
    return 'Folder created!'
  } else if (group.group_type === CUSTOM_CHANNEL) {
    return 'Channel created!'
  } else {
    return 'Inbox created!'
  }
}

/**
 * Creates a new group with the provided data and handles analytics.
 * @param group - Partial group data for creation
 * @returns Thunk that creates the group and dispatches analytics events
 */
export function createGroup(
  group: Partial<Group>,
): (dispatch: any) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: GROUP_CREATE,
      group,
    })

    try {
      const result = await api.createGroup(group)
      dispatch({
        type: GROUP_CREATE_SUCCESS,
        group: result,
      })

      if (group.parent) {
        dispatch(sendMixpanelEvent('Add sub-folder to channel'))
      }

      const managers = group.managers || []
      const members = group.members || []

      if (managers.length > 1 || members.length > 0) {
        if (group.group_type === CUSTOM_CHANNEL) {
          dispatch(sendMixpanelEvent('Change channel permissions'))
        }

        if (group.group_type && INBOX_TYPES.indexOf(group.group_type) > -1) {
          dispatch(sendMixpanelEvent('Change inbox permissions'))
        }
      }

      pushUINotification({
        message: groupCreateMessage(result),
        level: 'success',
      })
      return result
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      return dispatch({
        type: GROUP_CREATE_FAIL,
        group,
        error,
      })
    }
  }
}
/**
 * Loads business invites for a specific channel/group.
 * @param channelId - The ID of the channel to load invites for
 * @returns Thunk that fetches and dispatches business invites
 */
export function loadGroupBusinessInvites(
  channelId: GroupId,
): (dispatch: (...args: Array<any>) => any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: LOAD_GROUP_BUSINESS_SHARES,
    })

    try {
      const invites = await api.getGroupInvites(channelId)

      if (invites) {
        dispatch({
          type: LOAD_GROUP_BUSINESS_SHARES_SUCCESS,
          invites,
          groupId: channelId,
        })
      }
    } catch (error) {
      console.error('Error fetching businesses:', error)
    }
  }
}
/**
 * Sends a business invitation to join a channel/group.
 * @param channelId - The ID of the channel to invite to
 * @param subdomain - The subdomain of the business to invite
 * @returns Thunk that sends the invite and shows success/error notifications
 */
export function sendGroupBusinessInvite(
  channelId: GroupId,
  subdomain: string,
): (dispatch: (...args: Array<any>) => any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: GROUP_BUSINESS_SHARE_UPDATE,
    })

    try {
      const invites = await api.sendGroupInvites(channelId, {
        subdomain,
      })

      if (invites) {
        dispatch({
          type: GROUP_BUSINESS_SHARE_UPDATE_SUCCESS,
          invites,
          groupId: channelId,
        })
        pushUINotification({
          message: `Invitation sent to '${invites.name}'!`,
          level: 'success',
        })
      }
    } catch (error) {
      console.error('Error creating group invite:', error)
      pushUINotification({
        message: `Sorry! Invitation could not be sent, ${error.body?.subdomain?.[0]?.toLowerCase()}.`,
        level: 'error',
      })
    }
  }
}
/**
 * Resends a business invitation to join a channel/group.
 * @param channelId - The ID of the channel to resend invite for
 * @param subdomain - The subdomain of the business to resend invite to
 * @returns Thunk that resends the invite and shows notifications
 */
export function resendGroupBusinessInvite(
  channelId: GroupId,
  subdomain: string,
): (dispatch: (...args: Array<any>) => any) => Promise<void> {
  return async (_dispatch: (...args: Array<any>) => any) => {
    try {
      const invites = await api.sendGroupInvites(channelId, {
        subdomain,
      })
      pushUINotification({
        message: `Invitation re-sent to '${invites.name}'!`,
        level: 'success',
      })
    } catch (error) {
      console.error('Error resending group invite:', error)
      pushUINotification({
        message: `Sorry! Invitation could not be re-sent, ${error.body?.subdomain?.[0]?.toLowerCase()}.`,
        level: 'error',
      })
    }
  }
}
/**
 * Removes a business invitation from a channel/group.
 * @param channelId - The ID of the channel to remove invite from
 * @param inviteId - The ID of the invitation to remove
 * @param domain - The domain name for notification purposes
 * @returns Thunk that removes the invite and shows notifications
 */
export function removeGroupBusinessInvite(
  channelId: GroupId,
  inviteId: number,
  domain: string,
): (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: GROUP_BUSINESS_SHARE_UPDATE,
    })
    const channel = getGroup(getState(), {
      groupId: channelId,
    })
    const channelName = channel?.name ? `'${channel.name}'` : `channel`
    if (!inviteId || !domain) return

    try {
      await api.removeGroupInvites(channelId, {
        invite_id: inviteId,
      })
      dispatch({
        type: GROUP_BUSINESS_SHARE_UPDATE_SUCCESS,
        inviteId,
        groupId: channelId,
      })
      pushUINotification({
        message: `${domain} has been removed from ${channelName}.`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: `Sorry! Invitation could not be removed.`,
        level: 'error',
      })
      console.error('Error removing group invite:', error)
    }
  }
}
